import { useEffect, useState } from "react";
import { api, apiError } from "../lib/api";
import { PageHeader, Card } from "../components/UI";
import { Target, CheckCircle2, AlertTriangle, ClipboardList, Gauge } from "lucide-react";
import SurveysPanel from "./SurveysPanel";
import { V2Preview } from "../components/V2Preview";
import { toast } from "sonner";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";

function MeasureCard({ m }) {
  const hasTarget = m.target !== null && m.target !== undefined;
  const pass = !hasTarget ? null : m.invert ? m.value <= m.target : m.value >= m.target;
  return (
    <Card className="p-5" data-testid={`qm-card-${m.key}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="text-xs font-semibold uppercase tracking-wide text-[#7B6157]">{m.name}</div>
        {pass !== null && (pass
          ? <CheckCircle2 size={17} className="shrink-0 text-[#2E7D32]" />
          : <AlertTriangle size={17} className="shrink-0 text-[#B84A00]" />)}
      </div>
      <div className="mt-2 flex items-baseline gap-2">
        <span className={`font-heading text-3xl font-bold ${pass === false ? "text-[#B84A00]" : "text-[#3E2723]"}`} data-testid={`qm-value-${m.key}`}>{m.value}{m.unit}</span>
        {hasTarget && <span className="text-xs text-[#84675B]">target {m.invert ? "≤" : "≥"} {m.target}{m.unit}</span>}
      </div>
      <div className="mt-1.5 text-xs text-[#7B6157]">{m.detail}</div>
    </Card>
  );
}

export default function QualityMeasures() {
  const [data, setData] = useState(null);
  const [view, setView] = useState("surveys");
  useEffect(() => { api.get("/quality-measures").then((r) => setData(r.data)).catch((e) => toast.error(apiError(e))); }, []);
  return (
    <div data-testid="quality-measures-page">
      <PageHeader title="Quality Outcome Measures" subtitle="Clinic-wide quality, satisfaction, and outcomes program" />
      <div className="mb-5 flex flex-wrap gap-2">
        {[["surveys", "Surveys", ClipboardList, false], ["scorecard", "Scorecard", Gauge, true]].map(([k, l, Icon, v2]) => (
          <button key={k} data-testid={`qm-view-${k}`} onClick={() => setView(k)}
            className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${view === k ? "bg-[#4E342E] text-white" : "bg-[#F5F2EB] text-[#6D4C41] hover:bg-[#EDE7DC]"}`}>
            <Icon size={14} /> {l}
            {v2 && <span className={`ml-1 rounded-full px-1.5 py-0.5 text-[9px] font-bold ${view === k ? "bg-white text-[#4E342E]" : "bg-[#FFB300] text-[#3E2723]"}`}>V2</span>}
          </button>
        ))}
      </div>
      {view === "surveys" && <SurveysPanel />}
      {view === "scorecard" && (
        <V2Preview label="Scorecard"
          description="Clinic-wide quality outcome scorecard. Version 2 will layer live target tracking, per-program filtering, longitudinal trends, and automatic CARF export.">
          {!data ? <div className="py-16 text-center text-sm text-[#84675B]">Computing quality measures...</div> : (
            <>
              <div className="mb-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {data.measures.map((m) => <MeasureCard key={m.key} m={m} />)}
              </div>
              <Card className="p-5" data-testid="qm-chart">
                <h3 className="mb-3 flex items-center gap-2 font-heading text-sm font-bold text-[#3E2723]"><Target size={15} /> Performance vs. target</h3>
                <div className="h-72">
                  <ResponsiveContainer>
                    <BarChart data={data.measures.filter((m) => m.unit === "%" && m.target !== null).map((m) => ({ name: m.name.replace(/\(.*\)/, "").trim(), Actual: m.value, Target: m.target }))}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#EFEBE0" />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} /><YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                      <Tooltip /><Legend wrapperStyle={{ fontSize: 12 }} />
                      <Bar dataKey="Actual" fill="#4E342E" radius={[6, 6, 0, 0]} />
                      <Bar dataKey="Target" fill="#D9C7B2" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Card>
            </>
          )}
        </V2Preview>
      )}
    </div>
  );
}
